import { ApiBase } from '@bymason/api-base';
import { ScopeModel } from '../permission/types';
import {
  ApiKeysModel,
  CreateApiKeyModel,
  CreateApiKeyResponse,
  CreateScopedSessionModel,
  ScopedSessionModel,
} from './types';
export * from './types';

class ApiKeys extends ApiBase {
  private static instance: ApiKeys;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns An ApiKeys instance
   */
  public static getInstance(): ApiKeys {
    if (!ApiKeys.instance) {
      ApiKeys.instance = new ApiKeys();
      ApiKeys.instance.configure({
        fleet: 'apikey',
      });
    }

    return ApiKeys.instance;
  }

  /**
   * Gets all Api keys associated with the account.
   * @param options metadata required to get an ApI Keys.
   * @param clientToken token required for proper authorization.
   * @returns either ApiKeysModel[] or void
   */
  public async getApiKeys(clientToken: string) {
    const url = this.getEndpointTemplate('');

    const dataResponse = await this.callJsonEndpoint<ApiKeysModel[]>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets Api key scopes.
   * @param options metadata required to get an Api Keys.
   * @param clientToken token required for proper authorization.
   * @returns either ScopeModel[] or void
   */
  public async getApiKeyScopes(clientToken: string) {
    const url = this.getEndpointTemplate('scope');

    const dataResponse = await this.callJsonEndpoint<ScopeModel[]>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Deletes an Api key.
   * @param options metadata required to get an Api Keys.
   * @param clientToken token required for proper authorization.
   * @returns either ScopeModel[] or void
   */
  public async deleteApiKey(id: string, clientToken: string) {
    const url = this.getEndpointTemplate(id);

    const dataResponse = await this.callVoidEndpoint({
      clientToken,
      method: 'DELETE',
      url,
    });

    return dataResponse;
  }

  /**
   * Creates an Api key.
   * @param options metadata required to create an Api Keys.
   * @param clientToken token required for proper authorization.
   * @returns either ScopeModel[] or void
   */
  public async createApiKey(options: CreateApiKeyModel, clientToken: string) {
    const url = this.getEndpointTemplate('');

    const dataResponse = (await this.callJsonEndpoint)<CreateApiKeyResponse>({
      body: JSON.stringify(options),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  /**
   * Creates a scoped session.
   * @param options metadata required to create a scoped session.
   * @param clientToken token required for proper authorization.
   * @returns either ScopedSessionModel or void
   */
  public async createScopedSession(options: CreateScopedSessionModel, clientToken: string) {
    const url = this.getEndpointTemplate('session');

    const dataResponse = (await this.callJsonEndpoint)<ScopedSessionModel>({
      body: JSON.stringify(options),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }
}

export const apiKeysClient = ApiKeys.getInstance();
